import { useMemo, useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getMessagesByConversationId } from '@/features/messages/messages.service';
import { Message, TimelineItem } from '@/features/messages/messages.types';
import { conversationsQueryKeys } from '@/features/conversations/conversations.hook';

interface UseInfiniteMessagesProps {
  conversationId: string | null;
  initialPageSize?: number;
  enabled?: boolean;
}

interface UseInfiniteMessagesReturn {
  messages: Message[];
  timeline: TimelineItem[];
  loading: boolean;
  hasMore: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  error: Error | null;
  refreshMessages: () => void;
  hasPreviousPage: boolean;
}

export const useInfiniteMessages = ({
  conversationId,
  initialPageSize = 10,
  enabled = true,
}: UseInfiniteMessagesProps): UseInfiniteMessagesReturn => {
  const queryClient = useQueryClient();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['messages', 'infinite', conversationId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!conversationId) throw new Error('No conversation ID provided');

      const response = await getMessagesByConversationId({
        conversationId,
        page: pageParam,
        perPage: initialPageSize,
        sort: 'created_at',
        order: 'DESC', // Fetch newest first from API
      });

      return {
        data: response.data || [],
        page: pageParam,
        hasMore: response.data.length === initialPageSize,
        total: response.total || response.pageInfo?.totalItems || 0,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.page + 1 : undefined;
    },
    getPreviousPageParam: (firstPage) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    enabled: enabled && !!conversationId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 10000, // Refetch every 10 seconds
    refetchIntervalInBackground: false, // Only refetch when window is focused
    initialPageParam: 1,
  });

  // Flatten pages and maintain chronological order (oldest first for display)
  const messages = useMemo(() => {
    if (!data?.pages) return [];

    // Since API returns newest first (DESC order), reverse each page and then flatten
    // This maintains the proper order: oldest messages first, newest last
    const allMessages = data.pages
      .map((page) => [...page.data].reverse()) // Reverse each page individually
      .reverse() // Reverse pages order (oldest pages first)
      .flat(); // Flatten into single array

    return allMessages;
  }, [data]);

  // Convert messages to timeline format
  const timeline = useMemo((): TimelineItem[] => {
    return messages.map((message) => ({
      id: message.id,
      type: 'message' as const,
      content: message,
      created_at: message.created_at || new Date().toISOString(),
    }));
  }, [messages]);

  // Wrapper for fetchNextPage to handle loading state
  const handleFetchNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Refresh messages (refetch first page and reset)
  const refreshMessages = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: conversationsQueryKeys.all,
    });
    await refetch();
  }, [queryClient, refetch]);

  return {
    messages,
    timeline,
    loading: isLoading,
    hasMore: hasNextPage || false,
    fetchNextPage: handleFetchNextPage,
    isFetchingNextPage,
    error: error as Error | null,
    refreshMessages,
    hasPreviousPage,
  };
};
